import instance from "@store/middleware/api";

export interface UserChannelDto {
  id: string;
  userId: string;
  youtubeChannelId: string;
  channelHandle: string | null;
  channelName: string | null;
  channelThumbnailUrl: string | null;
  connectedAt: string;
  updatedAt: string;
}

export interface UserChannelsListResponse {
  channels: UserChannelDto[];
  cap: number;
}

export interface UserChannelsError {
  code: string;
  message: string;
  current?: number;
  cap?: number;
  input?: string;
}

export async function getUserChannels(): Promise<UserChannelsListResponse> {
  const { data } = await instance.get<UserChannelsListResponse>("/user-channels");
  return data;
}

export interface ResolvedChannelDto {
  channelId: string;
  handle: string | null;
  name: string;
  thumbnailUrl: string | null;
  canonicalUrl: string;
}

/** Resolve a YouTube channel input (handle, URL, video URL, channel ID) to
 *  its canonical metadata WITHOUT creating a user_channels row. Used by the
 *  preview/confirmation step before the user commits via connectUserChannel.
 *  The backend's 1-hour cache means the follow-up connect call won't hit
 *  the YouTube API a second time. */
export async function resolveUserChannel(input: string): Promise<ResolvedChannelDto> {
  const { data } = await instance.post<ResolvedChannelDto>("/user-channels/resolve", { input });
  return data;
}

/** Connect a channel into a SLOT. `slotIndex` is the empty slot (0..cap-1) the
 *  user picked; omit to auto-fill the lowest free slot. The channel inherits
 *  whatever monthly usage that slot already holds (slot-based credits). */
export async function connectUserChannel(input: string, slotIndex?: number): Promise<UserChannelDto> {
  const { data } = await instance.post<UserChannelDto>("/user-channels", { input, slotIndex });
  return data;
}

export async function disconnectUserChannel(id: string): Promise<void> {
  await instance.delete(`/user-channels/${id}`);
}

/** The channel currently occupying a slot (null = empty slot). */
export interface SlotChannel {
  id: string;
  youtubeChannelId: string;
  channelName: string | null;
  channelHandle: string | null;
  channelThumbnailUrl: string | null;
}

/** One slot in the slot-based credit model. The monthly usage belongs to the
 *  slot, not the channel — an empty slot can still carry a retained used-count
 *  that the next channel connected there inherits. See slot-based-credits.md. */
export interface ChannelSlot {
  slotIndex: number;
  channel: SlotChannel | null;
  used: number;
  limit: number;
  remaining: number;
  atLimit: boolean;
  /** ISO timestamp of when the current credit cycle ends (next reset). */
  cycleEnd: string | null;
}

export interface MyChannelCreditsResponse {
  /** Always all N slots (N = the plan's channel cap), filled or empty. */
  slots: ChannelSlot[];
  cap: number;
  connectedChannelCount: number;
}

/** Per-slot claim-credit usage for the current billing cycle — the data source
 *  for the claims-page slot panel. Always returns all N slots. Self-scoped. */
export async function getMyChannelCredits(): Promise<MyChannelCreditsResponse> {
  const { data } = await instance.get<MyChannelCreditsResponse>("/claims/my/channel-credits");
  return data;
}

/** Extract the structured `{ code, message, ... }` payload our backend returns
    for HttpException errors. Falls back to a generic shape if the response
    isn't structured. */
export function parseChannelError(err: any): UserChannelsError {
  // The error interceptor rejects with `error.response.data` itself, so the
  // payload usually IS the error object. Accept either shape — reading only
  // err.response.data silently produced "unknown_error" for every refusal the
  // backend took the trouble to name.
  const data = err?.response?.data ?? err;
  if (data && typeof data === "object" && data.code) {
    return data as UserChannelsError;
  }
  return {
    code: "unknown_error",
    message: err?.message || "Something went wrong. Please try again.",
  };
}
